import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import { authService } from "../services/api/authService";

const resetPasswordSchema = z
  .object({
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
  });

  useEffect(() => {
    if (!token) {
      setError(
        "Invalid or missing reset token. Please request a new password reset link.",
      );
      toast.error("Invalid reset token");
    }
  }, [token]);

  const onSubmit = async (data) => {
    if (!token) {
      setError("Invalid or missing reset token");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await authService.resetPassword(token, data.password);
      setIsSuccess(true);
      toast.success(
        "Password reset successfully! You can now login with your new password.",
      );

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to reset password. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-success/20 rounded-full mb-4">
              <Icon icon="mdi:check-circle" className="text-4xl text-success" />
            </div>
            <h1 className="text-3xl font-bold text-text-dark mb-2">
              Password Reset Successful
            </h1>
            <p className="text-text-secondary">
              Your password has been reset successfully. Redirecting to login...
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-border-light text-center">
            <Link to="/login" className="block">
              <Button className="w-full">Go to Login</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <img
            src="logo.png"
            alt="SentryPrime"
            className="mx-auto h-14 w-auto mb-3"
          />
          <p className="text-text-secondary">Reset Your Password</p>
        </div>

        {/* Reset Password Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border-light">
          <h2 className="text-2xl font-semibold text-text-dark mb-2">
            Set New Password
          </h2>
          <p className="text-sm text-text-secondary mb-6">
            Enter your new password below.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="New Password"
              type="password"
              placeholder="Enter your new password"
              showPasswordToggle
              {...register("password")}
              error={errors.password?.message}
            />

            <Input
              label="Confirm New Password"
              type="password"
              placeholder="Confirm your new password"
              showPasswordToggle
              {...register("confirmPassword")}
              error={errors.confirmPassword?.message}
            />

            <Button
              type="submit"
              disabled={isLoading || !token}
              className="w-full"
              icon="mdi:lock-reset"
            >
              {isLoading ? "Resetting Password..." : "Reset Password"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center justify-center gap-2"
            >
              <Icon icon="mdi:arrow-left" />
              Back to Login
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-text-secondary">
          <p>© 2024 SentryPrime. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
